import { Link } from "react-router-dom";
import { UserRound } from "lucide-react";
import {
  onboarding,
  SUBMISSION_STATUS_GROUPS,
  type OnboardingSubmissionRow,
  type OnboardingSubmissionSource,
} from "@/features/onboarding/api/onboarding.api";
import { applicationsV2 } from "@/features/applications/api/applications.api";
import { formatDateTime } from "@/utils/dates";
import {
  StatusPill,
  APPLICATION_STATUS_TONES,
  APPLICATION_STATUS_LABELS,
} from "@/components/StatusPill";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dash } from "@/components/Dash";
import { DateRangeFilter, useDateRangeFilter } from "@/components/DateRangeFilter";
import { useFileViewer } from "@/components/FileViewerDialog";
import { FilterBar } from "@/components/FilterBar";
import { AgentIdSelect } from "@/features/onboarding/components/AgentSelect";
import { Notice } from "@/components/Notice";
import { Pagination } from "@/components/Pagination";
import { SortableTH } from "@/components/SortableTH";
import { useBackOrigin } from "@/hooks/useBackTo";
import { useColumnSort } from "@/hooks/useColumnSort";
import { usePagedList } from "@/hooks/usePagedList";
import { useStatusCounts } from "@/hooks/useStatusCounts";
import { useUrlParam } from "@/hooks/useUrlParam";
import { cn } from "@/lib/utils";

type SortKey = "submittedAt" | "business" | "status";

const COLUMN_MAP: Record<SortKey, string> = {
  submittedAt: "submittedAt",
  business: "dbaName",
  status: "status",
};

const STATUS_TABS = Object.keys(SUBMISSION_STATUS_GROUPS);

const ALL_AGENTS = "all";
const ALL_AGENTS_OPTIONS = [{ value: ALL_AGENTS, label: "All agents" }];

export default function SubmittedFormsList({
  source,
  heading,
  description,
  hasAgents,
}: {
  source: OnboardingSubmissionSource;
  heading: string;
  description?: string;
  hasAgents?: boolean;
}) {
  const origin = useBackOrigin();
  const [agentId, setAgentId] = useUrlParam("agent", "");
  const { range, setRange, since, active: dateFiltered } = useDateRangeFilter({ syncUrl: true });

  const {
    data, loading, loaded, error, forbidden,
    keyword, status, sortBy, sortDir,
    setPage, setKeyword, setStatus, setSort,
  } = usePagedList<OnboardingSubmissionRow>(onboarding.submissions, {
    pageSize: 10,
    initialSortBy: "submittedAt",
    initialSortDir: "desc",
    extra: { source, agentId: agentId || undefined, since },
    syncUrl: true,
  });

  const counts = useStatusCounts(onboarding.submissionsSummary, SUBMISSION_STATUS_GROUPS, {
    source,
    keyword,
    agentId: agentId || undefined,
    since,
  });

  const { thProps } = useColumnSort<SortKey>(COLUMN_MAP, sortBy, sortDir, setSort);

  if (forbidden) return null;

  const rows = data.items;
  const filtered = keyword.trim() !== "" || status !== "" || agentId !== "" || dateFiltered;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b py-3 flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base font-semibold text-slate-900">{heading}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {loaded && !error && (
          <Badge variant="info" className="shrink-0 tabular-nums">
            {counts?.all ?? data.totalCount} total
          </Badge>
        )}
      </CardHeader>

      <FilterBar
        className="mb-0 px-5 py-3 border-b border-slate-100"
        value={status || "all"}
        onValueChange={(v) => setStatus(v === "all" ? "" : v)}
        tabs={[
          { value: "all", label: "All", count: counts?.all },
          ...STATUS_TABS.map((t) => ({ value: t, label: t, count: counts?.[t] })),
        ]}
        search={keyword}
        onSearchChange={setKeyword}
        searchPlaceholder="Search by business, prospect, or email"
      >
        {hasAgents && (
          <AgentIdSelect
            value={agentId || ALL_AGENTS}
            onValueChange={(v) => setAgentId(v === ALL_AGENTS ? "" : v)}
            pinnedOptions={ALL_AGENTS_OPTIONS}
            icon={<UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />}
            className="w-[200px]"
            contentClassName="w-[260px]"
          />
        )}
        <DateRangeFilter value={range} onChange={setRange} />
      </FilterBar>

      {error ? (
        <div className="p-5">
          <Notice tone="error">{error}</Notice>
        </div>
      ) : !loaded ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500">Loading…</div>
      ) : data.totalCount === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500">
          {filtered
            ? "No submissions match these filters."
            : "No prospects have submitted your link yet. Share it above and submissions will appear here as they come in."}
        </div>
      ) : (
        <div className={cn("transition-opacity", loading && "opacity-60")}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTH {...thProps("submittedAt")}>Submitted</SortableTH>
                  <SortableTH {...thProps("business")}>Business</SortableTH>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">Prospect</TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">Agent</TableHead>
                  <SortableTH {...thProps("status")}>Status</SortableTH>
                  <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Files</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => {
                  const dl = s.documents.find((d) => d.documentType === "DriverLicense");
                  const ck = s.documents.find((d) => d.documentType === "VoidedCheck");
                  return (
                    <TableRow key={s.id}>
                      <TableCell className="text-slate-600 whitespace-nowrap">{formatDateTime(s.submittedAt, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }) || <Dash />}</TableCell>
                      <TableCell>
                        <Link to={`/applications/v2/${s.id}`} state={origin} className="font-medium text-slate-900 hover:underline">
                          {s.dbaName || s.legalName || "(no business name)"}
                        </Link>
                        {s.legalName && s.dbaName && s.legalName !== s.dbaName && (
                          <div className="text-xs text-slate-500">{s.legalName}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        <div>{s.contactName || <Dash />}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[200px]">{s.email || s.phone || ""}</div>
                      </TableCell>
                      <TableCell className="text-slate-700 text-xs">
                        {s.agentName ? (
                          <>
                            {s.agentNumber != null && (
                              <span className="font-mono text-slate-500 mr-1">#{s.agentNumber}</span>
                            )}
                            {s.agentName}
                          </>
                        ) : <Dash />}
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          value={s.status}
                          map={APPLICATION_STATUS_TONES}
                          labels={APPLICATION_STATUS_LABELS}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-2 text-xs">
                          {dl && <FileLink appId={s.id} docId={dl.id} label="DL" title="Driver's license" />}
                          {ck && <FileLink appId={s.id} docId={ck.id} label="Check" title="Voided check" />}
                          {!dl && !ck && <span className="text-slate-400 italic">none</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/applications/v2/${s.id}`}
                          state={origin}
                          className="text-sm font-medium text-brand-600 hover:underline whitespace-nowrap"
                        >
                          Review →
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Pagination page={data} onPageChange={setPage} busy={loading} />
        </div>
      )}
    </Card>
  );
}

function FileLink({ appId, docId, label, title }: { appId: string; docId: string; label: string; title: string }) {
  const viewFile = useFileViewer();
  return (
    <button
      type="button"
      onClick={() =>
        viewFile({
          name: title,
          load: () => applicationsV2.documentFile(appId, docId),
          errorMessage: "Couldn't open the document — the file may no longer be available.",
        })
      }
      title={title}
      className="text-brand-600 hover:text-brand-700 underline"
    >
      {label}
    </button>
  );
}
